import { listKeyKindAdmits, type OutcomeKind } from '@developerz/domain';
import { TASKS_PATH, type TaskRow } from './fleet';
import type { UtilKey } from './fleetUtilization';
import { workWord } from './work-words';

// The Fleet page's bottom (#6788, epic #6552), pure: the word a task row shows,
// the AI accounts counts, and the one read `/fleet/work?machine=` makes.

/**
 * `completed` means different things by outcome (doc 04): a `pr` task opened a PR,
 * but `report` / `ops_action` tasks deliver into run events, with no PR, so "PR
 * opened" would mislead. `pr_unfulfilled` (#3400, #3677) is what a `pr` row carries
 * after the completed hop landed with no linked PR: a NAMED failure, and the word
 * says so. The one table: the queue row and the /fleet summary both read it.
 */
export const COMPLETED_LABEL: Readonly<Record<OutcomeKind, string>> = {
  pr: 'fleet.task_status_completed',
  report: 'fleet.task_status_completed_report',
  ops_action: 'fleet.task_status_completed_ops_action',
  pr_unfulfilled: 'fleet.task_status_completed_pr_unfulfilled',
};

/** The translation key of a task's work word: completed by outcome, else by status. */
export function taskWorkWord(task: Pick<TaskRow, 'status' | 'outcome_kind'>): string {
  return task.status === 'completed' ? COMPLETED_LABEL[task.outcome_kind] : workWord(task.status);
}

/** What one AI account's cell says. */
export type AccountCell = 'in_use' | 'resting' | 'free';

/**
 * One account's cell. RESTING WINS: an account cooling with a claim still in flight
 * is out of quota for every new job, so it is resting, never also in use, and the
 * three cells partition the pool.
 */
export function accountCell(key: Pick<UtilKey, 'in_flight' | 'cooldown_until_iso'>): AccountCell {
  if (key.cooldown_until_iso !== null) return 'resting';
  return key.in_flight > 0 ? 'in_use' : 'free';
}

export type AiAccountCounts = {
  /** Every account in the utilization read, which serves non-revoked keys only. */
  readonly connected: number;
  readonly in_use: number;
  readonly resting: number;
};

/** The three counts, off {@link accountCell}, so they can never overlap. */
export function countAiAccounts(
  keys: readonly Pick<UtilKey, 'in_flight' | 'cooldown_until_iso'>[],
): AiAccountCounts {
  const cells = keys.map(accountCell);
  return {
    connected: keys.length,
    in_use: cells.filter((c) => c === 'in_use').length,
    resting: cells.filter((c) => c === 'resting').length,
  };
}

/**
 * The work page's `?machine=` value. `machine` is a runner id the SERVER filters by;
 * `invalid` is anything that is not one (the api 400s a non-uuid `runner_id` filter),
 * which reads no query and shows the unknown-machine empty state.
 */
export type MachineNarrow =
  | { readonly kind: 'all' }
  | { readonly kind: 'machine'; readonly id: string }
  | { readonly kind: 'invalid' };

export function machineNarrow(raw: string | undefined): MachineNarrow {
  const id = (raw ?? '').trim();
  if (id === '') return { kind: 'all' };
  return listKeyKindAdmits('uuid', id) ? { kind: 'machine', id } : { kind: 'invalid' };
}

/**
 * The queue read for a narrow: the account's whole queue, or one machine's work
 * filtered by the server (`GET /v1/tasks?filter=runner_id:eq:<id>`, #6558), never on
 * the client. `null` for an invalid id: no read.
 */
export function tasksPath(narrow: MachineNarrow): string | null {
  if (narrow.kind === 'invalid') return null;
  return narrow.kind === 'all' ? TASKS_PATH : `${TASKS_PATH}?filter=runner_id:eq:${narrow.id}`;
}
